import DocumentLink from '../documents/DocumentLink'
import { readerContextFromHref } from '../../lib/documentReader'
/* Fund Evidence — "how is it proven?" (Ale ruling 2026-09-05, flat v2).
 *
 * A PURE FLAT TABLE: one line per object + obligation, 30 px rows —
 *   OBJECT (the kind dot, the name in the object's colour, its identity
 *   inline: "entity · GmbH · CH", "position · holder › issuer"; a break
 *   chip on a position with an open break) · OBLIGATION (code + words) ·
 *   DOCUMENT (chip + kind; an owed line names the kind it expects) · PAGE ·
 *   DATED · VALID UNTIL · STATUS (✓ proven / ✓ waived · name · date /
 *   ✗ owed · age / O optional)
 * in the plain order: entities, instruments, positions, trades; then name
 * A–Z. No rowspan cells, no holder lines, no focused "up the chain" view,
 * no class chips. The toolbar: a find box and the count chips proven ·
 * owed · optional · waived; the tab sentence above carries the counts. The
 * layout is the pure `evidenceRows`. */

import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import type { DrawerTarget } from '../ObjectDrawer'
import type { RecordTablePayload } from '../RecordTable'
import type { TaskQueueItem } from '../../lib/tasks'
import { formatOfficerDate, humanizeToken } from '../../lib/officerFormat'
import { statusTitle, type Lens, type MatrixRow } from './fundArea'
import {
  evidenceCounts,
  filterEvidence,
  flatEvidence,
  tableDate,
  type EvDemand,
  type EvShow,
} from './evidenceRows'
import { breakWords } from './positionLedger'
import { RECON_TASK_TYPES, taskDelta } from './recordDerive'
import { OptionalChip } from '../OptionalEvidence'
import { useIncludeOptional } from '../optionalEvidence'

interface Props {
  fundId: string
  table: RecordTablePayload
  matrix: MatrixRow[]
  /** Open workbench tasks on this fund — a position with an open break
   *  carries the break chip beside its name. */
  tasks: TaskQueueItem[]
  onOpen: (target: DrawerTarget) => void
  /** The active rebuild lens: the rows are already narrowed to the
   *  rebuilt record; the caption says which. */
  lens?: Lens | null
  /** `?show=` — the chip the URL asks for (the chain-status headline's
   *  owed door). `null` = every line. */
  show: EvShow | null
  onShow: (next: EvShow | null) => void
}

const COLUMNS = 7
const CHIPS: [EvShow, string, string][] = [
  ['proven', 'proven', 'dim'],
  ['owed', 'owed', 'owe'],
  ['optional', 'optional', 'dim'],
  ['waived', 'waived', 'dim'],
]

function daysSince(iso: string | null, today: string): number | null {
  if (!iso) return null
  const a = Date.parse(iso.slice(0, 10))
  const b = Date.parse(today)
  if (Number.isNaN(a) || Number.isNaN(b)) return null
  return Math.max(0, Math.round((b - a) / 86_400_000))
}

export default function FundEvidenceTab({
  fundId,
  table,
  matrix,
  tasks,
  onOpen,
  lens,
  show,
  onShow,
}: Props) {
  const [query, setQuery] = useState('')
  const showMode: EvShow = show ?? 'all'
  const today = new Date().toISOString().slice(0, 10)
  /* when the ledger was read: the payload's arrival (the heartbeat re-read
   * moves it) */
  const [readAt, setReadAt] = useState(() => new Date())
  useEffect(() => setReadAt(new Date()), [table])

  /* "include optional" (Ale 2026-09-05) — the SAME switch the chain-status
   * block and the sign-off card read, so the three can never disagree. OFF
   * (the default) the Owed view is the mandatory demands, as ruled; ON, the
   * recommended ones are listed with them. No count moves either way. */
  const [withOptional] = useIncludeOptional()

  const lines = useMemo(() => flatEvidence(table, matrix), [table, matrix])
  const counts = useMemo(() => evidenceCounts(matrix), [matrix])
  const view = useMemo(
    () => filterEvidence(lines, showMode, query, withOptional),
    [lines, showMode, query, withOptional]
  )

  /* a position's open break: the chip beside its name (the door to the task) */
  const breakByRecord = useMemo(() => {
    const m = new Map<string, TaskQueueItem>()
    for (const t of tasks)
      if (RECON_TASK_TYPES.has(t.task_type) && t.target_id && !m.has(t.target_id))
        m.set(t.target_id, t)
    return m
  }, [tasks])

  const docHref = (d: EvDemand) =>
    d.document?.id
      ? `/v2/documents/${d.document.id}?fund=${encodeURIComponent(fundId)}&tab=evidence${
          d.page ? `&page=${d.page.replace(/^p\./, '').split('–')[0]}` : ''
        }`
      : null

  const statusCell = (d: EvDemand) => {
    if (d.status === 'waived')
      return (
        <span className="wvd" data-testid="ev-waived" title={d.waivedReason ?? undefined}>
          ✓ waived
          {(d.waivedBy || d.waivedAt) && (
            <span className="by status-detail">
              {d.waivedBy}
              {d.waivedRole ? ` (${d.waivedRole})` : ''}
              {d.waivedBy && d.waivedAt ? ' · ' : ''}
              {d.waivedAt && <time dateTime={d.waivedAt}>{tableDate(d.waivedAt)}</time>}
            </span>
          )}
        </span>
      )
    if (d.proven) return <span className="ok">✓ proven</span>
    if (d.optional)
      return (
        <span className="opt" data-testid="ev-optional">
          <b>O</b> optional
          {/* batch B (Ale 2026-09-04): the document to obtain is the one
              that closes the break — an UNMET demand on a plug trade says
              so, whether policy demands it or only recommends it. */}
          {d.onPlug ? (
            <span className="age soft" data-testid="ev-on-plug">
              {' '}
              · on a plug
            </span>
          ) : null}
        </span>
      )
    const days = d.status === 'open' ? daysSince(d.owedSince, today) : null
    return (
      <span className="miss">
        ✗ owed
        {d.status === 'requested' ? (
          <span className="age status-detail">
            requested {d.requestedAt ? formatOfficerDate(d.requestedAt) : ''}
          </span>
        ) : d.status === 'pending_signoff' ? (
          <span className="age soft"> · sign-off open</span>
        ) : days !== null ? (
          <span className="age"> · {days} d</span>
        ) : null}
        {/* the same note on the owed mark. */}
        {d.onPlug ? (
          <span className="age soft" data-testid="ev-on-plug">
            {' '}
            · on a plug
          </span>
        ) : null}
      </span>
    )
  }

  const chip = (k: EvShow, label: string, cls: string, n: number) => (
    <button
      key={k}
      type="button"
      className={`fa-cchip ${cls}${n === 0 ? ' zero' : ''}${showMode === k ? ' on' : ''}`}
      onClick={() => onShow(showMode === k ? null : k)}
      aria-pressed={showMode === k}
      data-testid="ev-show-chip"
      data-show={k}
      data-count={n}
    >
      <b>{n}</b>
      {label}
    </button>
  )
  const countOf: Record<EvShow, number> = {
    all: counts.total,
    proven: counts.proven,
    // batch B (Ale 2026-09-04): the Owed chip carries the count it FILTERS
    // to, so the chip and the lines under it visibly agree — with "include
    // optional" on, that is every unmet demand.
    owed: withOptional ? counts.owed : counts.owed - counts.optional,
    optional: counts.optional,
    waived: counts.waived,
  }

  return (
    <div data-testid="fund-evidence">
      <div className="fa-card">
        <div className="fa-toolbar fa-flatbar" data-testid="ev-bar">
          {lens ? (
            <span className="fa-lens" data-testid="rebuild-lens">
              {lens.label}
            </span>
          ) : null}
          <input
            className="fa-search fa-find"
            aria-label="Find in the evidence"
            placeholder="find — object, obligation, document"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="ev-filter"
          />
          {CHIPS.map(([k, label, cls]) => chip(k, label, cls, countOf[k]))}
        </div>
        {/* "include optional" gets its own line under the bar (Ale
            2026-09-05): the SAME switch the chain-status block and the
            sign-off card read, OFF by default. It changes what the Owed
            view LISTS — no count moves. */}
        <div className="fa-optbar">
          <OptionalChip n={counts.optional} testId="ev-optional-toggle" />
        </div>
        <div className="fa-flatwrap">
          <table
            className="fa-rt fa-flat fa-evd"
            data-testid="evidence-matrix"
            data-show={showMode}
          >
            <colgroup>
              <col style={{ width: '30.6%' }} />
              <col style={{ width: '15.6%' }} />
              <col style={{ width: '19.5%' }} />
              <col style={{ width: '3.3%' }} />
              <col style={{ width: '7.5%' }} />
              <col style={{ width: '7.5%' }} />
              {/* STATUS is the answer — it holds "✗ owed · 12 d" and a
                  waiver's signature without truncating the age. */}
              <col style={{ width: '16%' }} />
            </colgroup>
            <thead>
              <tr>
                <th>object</th>
                <th>obligation</th>
                <th>document</th>
                <th>page</th>
                <th>dated</th>
                <th className="valid">valid until</th>
                <th>status</th>
              </tr>
            </thead>
            <tbody>
              {view.map((d) => {
                const o = d.object
                const brk =
                  o.kind === 'position'
                    ? (o.ids.map((id) => breakByRecord.get(id)).find(Boolean) ?? null)
                    : null
                const delta = brk ? taskDelta(brk) : null
                return (
                  <tr
                    key={`${o.kind}:${o.id}:${d.code}`}
                    data-testid="ev-row"
                    data-object={o.id}
                    data-kind={o.kind}
                    data-code={d.code}
                    data-status={d.status}
                    data-proven={d.proven || undefined}
                    onClick={() => onOpen(o.target)}
                    title="opens the object"
                  >
                    <td
                      className="obj"
                      data-testid="ev-object"
                      data-kind={o.kind}
                      data-id={o.id}
                      title={`${o.name} · ${o.sub}`}
                    >
                      {o.kind === 'trade' ? (
                        <span className={`tarr ${o.colour}`} aria-hidden="true">
                          →
                        </span>
                      ) : (
                        <span className={`tdot ${o.colour}`} aria-hidden="true" />
                      )}
                      <b className={`oname ${o.colour}`}>{o.name}</b>
                      {brk ? (
                        <Link
                          className="fa-brkchip"
                          to={`/v2/workbench/reconciliation?fund=${encodeURIComponent(fundId)}&task=${brk.task_id}`}
                          onClick={(e) => e.stopPropagation()}
                          data-testid="ev-break-door"
                          title="opens the reconciliation task"
                        >
                          {delta !== null ? breakWords(delta) : 'break'} ›
                        </Link>
                      ) : null}
                      <span className="kd">
                        <span className="sep">·</span>
                        {o.sub}
                      </span>
                    </td>
                    <td className="dem" data-testid="ev-demand">
                      <code>{d.code}</code>
                      {d.words}
                    </td>
                    <td className="doc" data-testid="ev-document">
                      {d.document ? (
                        <>
                          {docHref(d) ? (
                            <DocumentLink
                              className="fa-docchip"
                              document={{ document_id: d.document.id as string }}
                              context={readerContextFromHref(docHref(d))}
                              onClick={(e) => e.stopPropagation()}
                              data-testid="ev-doc"
                              title="Open the document"
                            />
                          ) : (
                            <span className="fa-docchip still" data-testid="ev-doc">
                              Source reference unavailable
                            </span>
                          )}
                          {d.document.kind ? (
                            <>
                              {' '}
                              <span className="kind">{humanizeToken(d.document.kind)}</span>
                            </>
                          ) : null}
                        </>
                      ) : d.status === 'waived' ? (
                        <span className="faint">waived</span>
                      ) : d.proven ? (
                        <span className="fa-none">—</span>
                      ) : (
                        <span className="exp" data-testid="ev-expected">
                          {d.expectedKind}
                        </span>
                      )}
                    </td>
                    <td className={`mono pg${d.page ? '' : ' ns'}`} data-testid="ev-page">
                      {d.page ?? '—'}
                    </td>
                    <td className={`mono${d.dated ? '' : ' ns'}`} data-testid="ev-dated">
                      {tableDate(d.dated) ?? '—'}
                    </td>
                    <td className={`mono valid${d.validTo ? '' : ' ns'}`} data-testid="ev-valid">
                      {tableDate(d.validTo) ?? '—'}
                    </td>
                    <td
                      className="stat"
                      data-testid="ev-status"
                      data-status={d.status}
                      data-mandatory={d.mandatory || undefined}
                      title={
                        d.status === 'waived'
                          ? [
                              'waived',
                              d.waivedBy,
                              d.waivedAt ? tableDate(d.waivedAt) : null,
                              d.waivedReason,
                            ]
                              .filter(Boolean)
                              .join(' · ')
                          : statusTitle(d.status)
                      }
                    >
                      {statusCell(d)}
                    </td>
                  </tr>
                )
              })}
              {view.length === 0 ? (
                <tr data-testid="ev-empty">
                  <td colSpan={COLUMNS} className="ns">
                    {matrix.length === 0 ? 'nothing demanded yet' : 'nothing matches'}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <div className="fa-tfoot" data-testid="ev-foot">
          <span>
            <b>{counts.total}</b> demand{counts.total === 1 ? '' : 's'} on the fund,{' '}
            <b>{counts.proven}</b> proven · one line per object and obligation
          </span>
          <span className="rd">
            ledger read{' '}
            <b>
              {formatOfficerDate(readAt.toISOString())} ·{' '}
              {readAt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
            </b>
          </span>
        </div>
      </div>
    </div>
  )
}
